import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PlanResponse } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

export type PlanStatus = PlanResponse['status'];

export interface PlanFilters {
  statuses: PlanStatus[];
  search: string;
}

export function usePlans({ enabled = true, pollInterval = 15_000 }: { enabled?: boolean; pollInterval?: number } = {}) {
  const apiClient = useApiClient();
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [filters, setFiltersState] = useState<PlanFilters>({
    statuses: ['pending_approval', 'approved', 'executing'],
    search: '',
  });
  const inFlight = useRef(false);
  const loadedOnce = useRef(false);

  const fetch = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    apiClient
      .listPlans()
      .then((res) => {
        setPlans(res.data);
        loadedOnce.current = true;
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load plans');
        if (!loadedOnce.current) setStatus('error');
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [apiClient]);

  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;

  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    fetchRef.current();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchRef.current();
    }, pollInterval);
    return () => clearInterval(id);
  }, [enabled, pollInterval]);

  const setFilters = useCallback((f: PlanFilters) => setFiltersState(f), []);

  const filtered = useMemo(() => {
    let result = [...plans];
    const f = filters;
    if (f.statuses.length > 0 && f.statuses.length < 7) {
      const allowed = new Set(f.statuses);
      result = result.filter((p) => allowed.has(p.status));
    }
    if (f.search) {
      const q = f.search.toLowerCase();
      result = result.filter(
        (p) =>
          p.id.toLowerCase().includes(q) ||
          (p.conversation_id ?? '').toLowerCase().includes(q) ||
          (p.task_id ?? '').toLowerCase().includes(q),
      );
    }
    result.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return result;
  }, [plans, filters]);

  const counts = useMemo(() => {
    const c: Partial<Record<PlanStatus, number>> = {};
    for (const p of plans) c[p.status] = (c[p.status] ?? 0) + 1;
    return c;
  }, [plans]);

  const approvePlan = useCallback(
    async (id: string) => {
      const approved = await apiClient.approvePlan(id);
      setPlans((prev) => prev.map((p) => (p.id === id ? approved : p)));
      return approved;
    },
    [apiClient],
  );

  const denyPlan = useCallback(
    async (id: string, reason?: string) => {
      const denied = await apiClient.denyPlan(id, reason);
      setPlans((prev) => prev.map((p) => (p.id === id ? denied : p)));
      return denied;
    },
    [apiClient],
  );

  const reload = useCallback(() => {
    inFlight.current = false;
    fetchRef.current();
  }, []);

  return {
    plans: filtered,
    allPlans: plans,
    status,
    error,
    filters,
    setFilters,
    counts,
    approvePlan,
    denyPlan,
    reload,
  };
}
